import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../store';
import useTranslations from '../../../hooks/useTranslations';
import useGetGridData from '../../../hooks/useGetGridData';
import useUrlSearchParam from '../../../hooks/useUrlSearchParams';
import { FieldTypes, IFormFields } from '../../../models/form';
import { IAutoCompleteItem } from '../../../models/autoComplete';
import { ISite } from '../../../models/site';
import Filters from '../../../components/Container/Filters/Filters';
import EnhancedTable from '../../../components/UI/RcTable/EnhancedTable';
import TableButtons from '../../../components/UI/TableButtons/TableButtons';
import ActionButtons from '../../../components/UI/ActionButtons/ActionButtons';
import Title from '../../../components/UI/Title/Title';
import { ActionPermissionsActions } from '../../../store/entities/actionPermissions/actionPermissions.actions';
import { SitesActions } from '../../../store/entities/sites/sites.actions';
import { GeneralActions } from '../../../store/entities/general/general.actions';
import { IPage } from '../../../models/page';
import { TableActions } from '../../../store/entities/table/table.actions';
import { getDateTimeAndSeconds, sortDataByDate } from '../../../utils/formatters';
import {
  getCustomerSite,
  getCityById,
  getShippingCompanyNameById,
} from '../../../utils/addressFormatters';
import { ICity } from '../../../store/entities/general/type';
import { IActionPermission } from '../../../models/actionPermission';
import { actionPermissionsListSearchValidation } from '../../../utils/formValidation/actionPermissionsList';
import { PERMISSIONS } from '../../../utils/permissions';
import { CONSTANTS } from '../../../utils/constants';
import { ACTION_PERMISSION_STATUS_OPTIONS } from '../actionPermissionsConstants';
import { ShippingCompaniesActions } from '../../../store/entities/shippingCompanies/shippingCompanies.actions';

const ActionPermissionsList = ({ permission, nextRoute }: IPage) => {
  const [rows, setRows] = useState<any[]>([]);
  const [siteList, setSiteList] = useState<IAutoCompleteItem[]>([]);
  const [cityList, setCityList] = useState<IAutoCompleteItem[]>([]);
  const [shippingCompanyList, setShippingCompanyList] = useState<IAutoCompleteItem[]>([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rcTranslate } = useTranslations();
  const { onPageChange } = useGetGridData();
  const { getValueFromQuery, params } = useUrlSearchParam();

  const { actionPermissions } = useAppSelector((state) => state.actionPermissions);
  const { nextPageNumber, currentPage, sortModel } = useAppSelector((state) => state.table);
  const { formikProps } = useAppSelector((state) => state.form);
  const { appPermissions }: { appPermissions: string[] } = useAppSelector((state) => state.auth);
  const { siteListDetails } = useAppSelector((state) => state.sites);
  const { cities, appLanguage } = useAppSelector((state) => state.general);
  const { shippingCompanies } = useAppSelector((state) => state.shippingCompanies);

  useEffect(() => {
    if (permission === false) {
      navigate(nextRoute);
    }
  }, [permission, nextRoute, navigate]);

  useEffect(() => {
    if (permission) {
      if (currentPage !== 1) {
        dispatch(TableActions.setTableData({ currentPage: 1 }));
      }
      dispatch(ActionPermissionsActions.getActionPermissions(params));
      dispatch(SitesActions.getSiteListDetails());
      dispatch(GeneralActions.getCities());
      dispatch(ShippingCompaniesActions.getShippingCompanies());
    }
  }, [dispatch, params, permission]);

  useEffect(() => {
    if (nextPageNumber) {
      const getData = async () => {
        dispatch(
          ActionPermissionsActions.getActionPermissions(
            params,
            sortModel,
            false,
            nextPageNumber
          )
        );
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [dispatch, nextPageNumber, sortModel]);

  useEffect(() => {
    if (sortModel.length) {
      const getData = async () => {
        dispatch(ActionPermissionsActions.getActionPermissions(null, sortModel, false));
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [sortModel]);

  useEffect(() => {
    if (siteListDetails?.length) {
      const newSites: IAutoCompleteItem[] = [...siteListDetails]
        .sort((a: ISite, b: ISite) => (a.id > b.id ? 1 : -1))
        .map((site: ISite) => ({
          id: site.id,
          label: `${site.id} - ${
            appLanguage === CONSTANTS.DEFAULT_LANGUAGE
              ? site.nameHE
              : (site.nameEN ?? '')
          }`,
        }));
      setSiteList(newSites);
    }
  }, [appLanguage, siteListDetails]);

  useEffect(() => {
    if (cities?.length) {
      const newCities: IAutoCompleteItem[] = cities.map((city: ICity) => ({
        id: city.id,
        label:
          (appLanguage === CONSTANTS.DEFAULT_LANGUAGE
            ? city.nameHE
            : city.nameEN) ?? '',
      }));
      setCityList(newCities);
    }
  }, [appLanguage, cities]);

  useEffect(() => {
    if (shippingCompanies?.length) {
      const newShippingCompanies: IAutoCompleteItem[] = shippingCompanies.map(
        (shippingCompany) => ({
          id: shippingCompany.id,
          label:
            (appLanguage === CONSTANTS.DEFAULT_LANGUAGE
              ? shippingCompany.nameHE
              : shippingCompany.nameEN) ?? '',
        })
      );
      setShippingCompanyList(newShippingCompanies);
    }
  }, [appLanguage, shippingCompanies]);

  const columns: any[] = useMemo(
    () => [
      {
        id: 'permissionId',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.id'),
      },
      {
        id: 'inquiringCustomerName',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.inquiringCustomerName'),
      },
      {
        id: 'inquiringCustomerPhoneNumber',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.inquiringCustomerPhoneNumber'),
      },
      {
        id: 'authorizerCustomerName',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.authorizerCustomerName'),
      },
      {
        id: 'authorizerCustomerPhoneNumber',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.authorizerCustomerPhoneNumber'),
      },
      {
        id: 'shippingCompanyName',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.shippingCompanyName'),
      },
      {
        id: 'status',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.status'),
      },
      {
        id: 'cityId',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.cityId'),
      },
      {
        id: 'siteId',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.siteId'),
      },
      {
        id: 'cellId',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.cellId'),
      },
      {
        id: 'startDate',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.startDate'),
      },
      {
        id: 'expiryDate',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.expiryDate'),
      },
      {
        id: 'usesCounter',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.usesCounter'),
      },
      {
        id: 'usesLimit',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.usesLimit'),
      },
      {
        id: 'created',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.created'),
      },
      {
        id: 'statusChangedBy',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('actionPermissions.table.statusChangedBy'),
      },
      {
        id: 'actions',
        numeric: false,
        disablePadding: true,
        label: '',
      },
    ],
    [rcTranslate]
  );

  const openEdit = (id: number) => {
    navigate(
      `/${CONSTANTS.ROUTES.ACTION_PERMISSIONS_MANAGEMENT.BASE}/${CONSTANTS.ROUTES.ACTION_PERMISSIONS_MANAGEMENT.CHILDREN.ACTION_PERMISSIONS_UPDATE}/${id}`
    );
  };

  const mapActionPermissionsToTableRows = useCallback(
    (items: IActionPermission[]) => {
      return items.map((item: IActionPermission) => {
        return {
          id: item.id,
          permissionId: item.id,
          inquiringCustomerName: item.inquiringCustomerName ?? '',
          inquiringCustomerPhoneNumber: item.inquiringCustomerPhoneNumber ?? '',
          authorizerCustomerName: item.authorizerCustomerName ?? '',
          authorizerCustomerPhoneNumber: item.authorizerCustomerPhoneNumber ?? '',
          shippingCompanyName: item.shippingCompanyId
            ? getShippingCompanyNameById(item.shippingCompanyId, shippingCompanies, appLanguage)
            : '',
          status: item.status != null
            ? rcTranslate(`actionPermissions.statuses.${item.status}`)
            : '',
          cityId: item.cityId
            ? getCityById(item.cityId, cities, appLanguage)
            : '',
          siteId: item.siteId
            ? getCustomerSite(item.siteId, siteListDetails, appLanguage)
            : '',
          cellId: item.cellId ?? '',
          startDate: item.startDate ? getDateTimeAndSeconds(item.startDate) : '',
          expiryDate: item.expiryDate ? getDateTimeAndSeconds(item.expiryDate) : '',
          usesCounter: item.usesCounter ?? 0,
          usesLimit: item.usesLimit ?? '',
          created: item.created ? getDateTimeAndSeconds(item.created) : '',
          statusChangedBy: item.statusChangedByName ?? '',
          actions: (
            <ActionButtons
              menuItems={[
                {
                  label: 'actionPermissions.actions.edit',
                  onClick: () => openEdit(item.id),
                  show: appPermissions.includes(PERMISSIONS.UPDATE_ACTION_PERMISSION),
                },
              ]}
              hide={!appPermissions.includes(PERMISSIONS.UPDATE_ACTION_PERMISSION)}
            />
          ),
        };
      });
    },
    [rcTranslate, appPermissions, siteListDetails, cities, shippingCompanies, appLanguage]
  );

  useEffect(() => {
    const rows = actionPermissions?.length ? mapActionPermissionsToTableRows(actionPermissions) : [];
    setRows(rows);
  }, [actionPermissions, mapActionPermissionsToTableRows]);

  const searchFields: IFormFields = {
    initialValues: {
      inquiringPhoneNumber: getValueFromQuery('inquiringPhoneNumber'),
      authorizerPhoneNumber: getValueFromQuery('authorizerPhoneNumber'),
      statuses: getValueFromQuery('statuses'),
      from: getValueFromQuery('from'),
      to: getValueFromQuery('to'),
      inquiringCustomerId: getValueFromQuery('inquiringCustomerId'),
      authorizerCustomerId: getValueFromQuery('authorizerCustomerId'),
      shippingCompanyId: getValueFromQuery('shippingCompanyId', 'number'),
      cityId: getValueFromQuery('cityId', 'number'),
      siteId: getValueFromQuery('siteId', 'number'),
      cellId: getValueFromQuery('cellId'),
    },
    formData: [
      {
        type: FieldTypes.CHILDREN,
        id: 'externalFieldsWrapper',
        fields: [
          {
            type: FieldTypes.TEL,
            id: 'inquiringPhoneNumber',
            label: 'actionPermissions.filters.inquiringPhoneNumber',
            props: { inputProps: { maxLength: 14 } },
          },
          {
            type: FieldTypes.TEL,
            id: 'authorizerPhoneNumber',
            label: 'actionPermissions.filters.authorizerPhoneNumber',
            props: { inputProps: { maxLength: 14 } },
          },
          {
            type: FieldTypes.SELECT,
            id: 'statuses',
            label: 'actionPermissions.filters.status',
            items: ACTION_PERMISSION_STATUS_OPTIONS,
          },
          {
            type: FieldTypes.DATE,
            id: 'from',
            label: 'actionPermissions.filters.from',
            props: { getStartOfDay: true },
          },
          {
            type: FieldTypes.DATE,
            id: 'to',
            label: 'actionPermissions.filters.to',
            props: {
              getFullDayTime: true,
              numOfDays: 1,
              minDate: new Date(formikProps?.values?.from),
            },
          },
        ],
      },
      {
        type: FieldTypes.CHILDREN,
        id: 'extendedFieldsWrapper',
        fields: [
          {
            type: FieldTypes.CHILDREN,
            id: 'extendedFieldsContainer',
            fields: [
              {
                type: FieldTypes.CHILDREN,
                id: 'extendedFields',
                className: 'filters__extendedFields',
                fields: [
                  {
                    type: FieldTypes.TEXT,
                    id: 'inquiringCustomerId',
                    label: 'actionPermissions.filters.inquiringCustomerId',
                  },
                  {
                    type: FieldTypes.TEXT,
                    id: 'authorizerCustomerId',
                    label: 'actionPermissions.filters.authorizerCustomerId',
                  },
                  {
                    type: FieldTypes.AUTOCOMPLETE,
                    id: 'shippingCompanyId',
                    label: 'actionPermissions.filters.shippingCompanyId',
                    items: shippingCompanyList,
                    props: { multiple: false },
                  },
                  {
                    type: FieldTypes.AUTOCOMPLETE,
                    id: 'cityId',
                    label: 'actionPermissions.filters.cityId',
                    items: cityList,
                    props: { multiple: false },
                  },
                  {
                    type: FieldTypes.AUTOCOMPLETE,
                    id: 'siteId',
                    label: 'actionPermissions.filters.siteId',
                    items: siteList,
                    props: { multiple: false },
                  },
                  {
                    type: FieldTypes.TEXT,
                    id: 'cellId',
                    label: 'actionPermissions.filters.cellId',
                  },
                ],
              },
              {
                type: FieldTypes.CHILDREN,
                id: 'actionsContainer',
                className: 'filters__extendedActions',
                fields: [
                  {
                    type: FieldTypes.BUTTON,
                    id: 'closeExtendedSearch',
                    label: 'searchFields.closeExtendedSearch',
                  },
                  {
                    type: FieldTypes.CHILDREN,
                    id: 'submitButtonWrapper',
                    className: 'filters__submitButtonWrapper',
                    fields: [
                      {
                        type: FieldTypes.BUTTON,
                        id: 'cleanSearch',
                        label: 'searchFields.cleanSearch',
                      },
                      {
                        type: FieldTypes.SUBMIT_BUTTON,
                        id: 'submitButton',
                        label: '',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  return (
    <div className="pageContainer">
      <Title text="actionPermissions.pageTitle" />

      <Filters fields={searchFields} filtersValidation={actionPermissionsListSearchValidation} />

      <TableButtons
        generalButtons={[]}
        tableButtons={[]}
      />

      <div className="actionPermissionsList">
        <EnhancedTable
          rows={rows}
          columns={columns}
          sortDataByDate={sortDataByDate}
          checkboxSelection={false}
          expandable={false}
          showId={false}
        />
      </div>
    </div>
  );
};

export default ActionPermissionsList;
